import { Button, Field, Input } from '@aurea/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthCard } from '../components/AuthCard';
import { ApiError, api } from '../lib/api';
import { useErrorText } from '../lib/i18n';

export const Route = createFileRoute('/join/$token')({ component: Join });

function Join() {
  const { token } = Route.useParams();
  const { t } = useTranslation();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const [consent, setConsent] = useState(false);
  const info = useQuery({ queryKey: ['join', token], queryFn: () => api.joinInfo(token), retry: false });
  const join = useMutation({
    mutationFn: (b: Parameters<typeof api.join>[1]) => api.join(token, b),
    onSuccess: () => navigate({ to: '/' }),
  });

  const fields =
    join.error instanceof ApiError
      ? (join.error.details?.fields as Record<string, string[]> | undefined)
      : undefined;
  const emailTaken = join.error instanceof ApiError && join.error.code === 'CONFLICT';

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const v = (k: string) => String(f.get(k) ?? '').trim();
    join.mutate({
      firstName: v('firstName'),
      lastName: v('lastName'),
      email: v('email'),
      phone: v('phone') || undefined,
      password: String(f.get('password')),
      consent: true,
    });
  };

  if (info.isError)
    return (
      <AuthCard title={t('join.title')} lead={errorText(info.error)}>
        {null}
      </AuthCard>
    );
  const school = info.data?.school.name ?? '';
  const course = info.data?.courseName;
  return (
    <AuthCard
      title={t('join.title')}
      lead={
        info.data && (
          <>
            {t('join.invited', { school })}
            {course && (
              <>
                <br />
                {t('join.course', { course })}
                {info.data.groupName && `, ${t('join.group', { group: info.data.groupName })}`}
              </>
            )}
          </>
        )
      }
    >
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('join.firstName')} error={fields?.firstName && t('errors.VALIDATION_FAILED')}>
            <Input name="firstName" autoComplete="given-name" required autoFocus />
          </Field>
          <Field label={t('join.lastName')}>
            <Input name="lastName" autoComplete="family-name" required />
          </Field>
        </div>
        <Field
          label={t('join.email')}
          error={
            emailTaken ? t('join.emailTaken') : fields?.email ? t('errors.VALIDATION_FAILED') : undefined
          }
        >
          <Input name="email" type="email" autoComplete="email" required invalid={emailTaken} />
        </Field>
        <Field label={`${t('join.phone')} (${t('common.optional')})`}>
          <Input name="phone" type="tel" autoComplete="tel" />
        </Field>
        <Field label={t('join.password')} hint={t('setup.hint')}>
          <Input name="password" type="password" autoComplete="new-password" minLength={10} required />
        </Field>
        <label className="check">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required />
          {t('join.consent', { school })}
        </label>
        {join.isError && !emailTaken && !fields && (
          <p className="text-sm text-danger">{errorText(join.error)}</p>
        )}
        <Button type="submit" loading={join.isPending} disabled={!consent || !info.data}>
          {t('join.submit')}
        </Button>
        <Link to="/login" className="btn-link self-start">
          {t('join.toLogin')}
        </Link>
      </form>
    </AuthCard>
  );
}
